import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui';
import AssistantPanel, { type MorningRequest } from '../components/AssistantPanel';
import MorningBrief from '../components/morning/MorningBrief';
import {
  CallRow, DeadlineRow, LINK, MailRow, NOT_SAVED, Section, TextRow, TodoRow, Unreadable, type Dismiss,
} from '../components/morning/MorningRows';
import { splitDeadlines, wordsOf } from '../components/todos/todoFormat';
import { MorningChatContext, type MorningChat } from '../contexts/MorningChatContext';
import { keepChatHint, readChatHint } from '../lib/morningChatHint';
import {
  api, type MorningCall, type MorningMailItem, type MorningDismissKind, type MorningItemRef, type MorningSection,
  type MorningText, type MyMorning,
} from '../lib/api';

const NOT_LINKED = 'Your app login does not match a GoHighLevel user by email, so there is no morning list to show.';
const NOT_READ = "Couldn't read your morning list - try again.";
/** Item 7 (switched on only): a section that could not be read is read again after this long, while the page is visible. */
const REREAD_MS = 60_000;
/**
 * MORNING2: this device's last-known answer to "is the chat on for him?" - `readChatHint` / `keepChatHint`, which moved
 * to `lib/morningChatHint.ts` (CHAT-DUET) so the phone bar can read it on every page. Same key, same rules: ⛔ absent or
 * unreadable is OFF; a hint, never a fact.
 */

interface DismissState { busy: boolean; error: string | null }

/** A section on the page could not be read (the switched-on page reads it again in a minute). */
const somethingUnread = (m: MyMorning | null): boolean => !!m && m.linked
  && [m.mail, m.texts, m.calls, m.todos, m.deadlines, m.radar].some((x) => x.status === 'unreadable');

/**
 * MORNING1 - the Morning page (spec 2): "who is waiting on me, and what did I promise?" - email, texts, missed calls,
 * to-dos due, deal deadlines, most urgent first, then ONE Deal Radar line that links to the radar (never rebuilt here).
 * Built for the phone first: every row wraps, nothing runs off a 390 px screen.
 *  - An empty section is not shown; ⛔ one the server could not read says so with Try again - never "nothing waiting".
 *  - ⛔ Loading only READS. Not needed, Done and Later are his taps, and a row changes only after the server's yes.
 *  - Item 7 (switched on only): while a section could not be read, the page reads again every minute it is visible,
 *    and keeps the words until a read succeeds. ⛔ A page with the chat off never reads by itself - today's page.
 *
 * MORNING2 (Task 9) - for a person switched on (`chat.on`, Joseph), the page is ONE screen in the Claude Duet style he
 * picked (2026-10-28): the app's own AI chat in `morning` mode, with the brief as its first block (the greeting, Needs
 * you, Can wait folded, the radar line), then the conversation, then the chat box at the bottom. A Start on a card is a
 * chat turn through the panel's own send; an email reply arrives as the send card, and a sent one reads the list again.
 * On /morning that chat is the only one: Layout renders no rail, and the top bar's Assistant goes to the chat box.
 * ⛔ Robert, and Joseph until he is switched on, keep today's page exactly - with `chat.words` above it when the setting
 * could not be read on a device that last saw him switched on (never for Robert).
 */
export default function Morning() {
  const [data, setData] = useState<MyMorning | null>(null);
  const [showOld, setShowOld] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Bumped on every good read: the to-do rows remount, so "Marked done. Undo" lasts until the next read and no longer.
  const [reads, setReads] = useState(0);
  // Bumped on every read, good or not: an unreadable page arms its next re-read from it (item 7).
  const [attempts, setAttempts] = useState(0);
  // Not needed: `kind:id` -> its tap's state; `gone` = the server said yes.
  const [dismissing, setDismissing] = useState<Record<string, DismissState>>({});
  const [gone, setGone] = useState<Record<string, true>>({});
  // Reply (5.3): the one email whose draft is open; `sent` = Gmail said yes. Both last until the next read.
  const [replying, setReplying] = useState<string | null>(null);
  const [sent, setSent] = useState<Record<string, true>>({});
  // MORNING2: his last tap on a Start (a new `seq` each tap), and whether an answer is arriving in the chat.
  const [request, setRequest] = useState<MorningRequest | undefined>(undefined);
  const [answering, setAnswering] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  /**
   * This device's last-known "the chat is on" (see CHAT_HINT_KEY): before the first read answers it decides the rail; after
   * every read it decides whether "Couldn't read your morning chat setting" is said (a person never seen switched on -
   * Robert - keeps today's page byte for byte when his setting read blips).
   */
  const [chatHint, setChatHint] = useState(readChatHint);

  // R59: the last good read came from the automatic minute re-read (not a Refresh, Try again or a change he made). The brief
  // then re-asks AI card text only if the cards changed - the answered cards are not asked again every minute.
  const [autoRead, setAutoRead] = useState(false);

  const load = useCallback(async (auto = false) => {
    setLoading(true);
    try {
      const read = await api.morning.get();
      setData(read);
      setAutoRead(auto === true);
      // ⛔ A setting that could not be read (`on: false` WITH words) is not an answer: the hint keeps what it knew.
      const blip = !!(read.linked && read.chat && !read.chat.on && read.chat.words);
      if (!blip) keepChatHint(!!(read.linked && read.chat?.on));
      setChatHint(readChatHint());
      setReads((n) => n + 1);
      setDismissing({});
      setGone({});
      setSent({});
      setLoadError(null);
    } catch (e) {
      setLoadError(wordsOf(e, NOT_READ));
    } finally {
      setLoading(false);
      setAttempts((n) => n + 1);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  /**
   * Item 7: switched on, and a section could not be read - read again in a minute, while the page is visible (a hidden
   * page waits until he looks). Every read re-arms it, a failed one too, so the words stay until a read succeeds; then
   * it stops. ⛔ Only switched on: today's page (Robert) never reads by itself, so his "Marked done. Undo" is never reset.
   */
  const unread = !!(data?.linked && data.chat?.on) && somethingUnread(data);
  useEffect(() => {
    if (!unread) return;
    const whenVisible = () => {
      if (document.visibilityState === 'hidden') return;
      document.removeEventListener('visibilitychange', whenVisible);
      void load(true);
    };
    const timer = setTimeout(() => {
      if (document.visibilityState === 'hidden') document.addEventListener('visibilitychange', whenVisible);
      else void load(true);
    }, REREAD_MS);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', whenVisible);
    };
  }, [unread, attempts, load]);

  const notNeeded = async (kind: MorningDismissKind, id: string) => {
    const key = `${kind}:${id}`;
    setDismissing((s) => ({ ...s, [key]: { busy: true, error: null } }));
    try {
      await api.morning.dismiss(kind, id);
      setGone((g) => ({ ...g, [key]: true }));
    } catch (e) {
      setDismissing((s) => ({ ...s, [key]: { busy: false, error: wordsOf(e, NOT_SAVED) } }));
    }
  };
  const dismissOf = (kind: MorningDismissKind, id: string): Dismiss => ({
    busy: dismissing[`${kind}:${id}`]?.busy ?? false,
    error: dismissing[`${kind}:${id}`]?.error ?? null,
    onDismiss: () => void notNeeded(kind, id),
  });

  const retry = () => void load();
  /** A section's body, or null when it has nothing (an empty section is not shown). */
  const body = <T,>(s: MorningSection<T>, rows: (items: T[]) => ReactNode, more?: ReactNode): ReactNode => {
    if (s.status === 'unreadable') return <Unreadable words={s.words} onRetry={retry} busy={loading} />;
    // ⭐ "and N more" stays when every shown row is marked - the rest still wait.
    if (s.items.length === 0 && !more) return null;
    return <>{s.items.length > 0 && rows(s.items)}{more}</>;
  };
  const moreLink = (n: number, to: string) => (n > 0 ? <Link to={to} className={`${LINK} mt-3 inline-block`}>{`and ${n} more`}</Link> : null);

  const textRow = (t: MorningText) => <TextRow key={t.conversation_id} t={t} dismiss={dismissOf('text', t.conversation_id)} />;
  const mailRow = (t: MorningMailItem) => (
    <MailRow
      key={t.thread_id}
      t={t}
      sent={!!sent[t.thread_id]}
      replying={replying === t.thread_id}
      onReply={() => setReplying(t.thread_id)}
      onCloseReply={() => setReplying(null)}
      onSent={() => { setReplying(null); setSent((x) => ({ ...x, [t.thread_id]: true })); }}
      dismiss={dismissOf('mail', t.thread_id)}
    />
  );
  const callRow = (c: MorningCall) => <CallRow key={c.conversation_id} c={c} dismiss={dismissOf('call', c.conversation_id)} />;

  const m = data?.linked ? data : null;
  /** MORNING2: switched on - the page is the chat. */
  const chat = m?.chat?.on ? m.chat : null;
  /**
   * MORNING2: switched off, but the setting could not be read - said above today's sections, and ONLY on a device that
   * last saw him switched on. ⛔ Robert (never switched on) gets today's page byte for byte when his setting read blips.
   */
  const chatWords = m?.chat && !m.chat.on && chatHint ? m.chat.words ?? null : null;

  const left = <T,>(s: MorningSection<T>, kind: MorningDismissKind, idOf: (x: T) => string): MorningSection<T> =>
    (s.status === 'ok' ? { ...s, items: s.items.filter((x) => !gone[`${kind}:${idOf(x)}`]) } : s);

  const mailLeft = m && m.mail.status === 'ok'
    ? { ...m.mail, items: m.mail.items.filter((t) => !gone[`mail:${t.thread_id}`]) } : null;
  // ⛔ Spec 3: a person with no Gmail grant has no email section at all (there is no in-app connect button to point to).
  const mail = m && (m.mail.status === 'not_connected'
    ? null
    : m.mail.status === 'unreadable' ? <Unreadable words={m.mail.words} onRetry={retry} busy={loading} />
      // ⭐ The rest past the 30 shown are in Gmail, so "and N more" opens Gmail - and stays when the shown are all marked.
      // ⛔ Past the 100 threads read, the count is a floor: "and more", never a number that looks whole.
      : mailLeft && (mailLeft.items.length > 0 || mailLeft.more > 0 || mailLeft.truncated) ? (
        <>
          {mailLeft.items.length > 0 && <ul aria-label="Email waiting" className="space-y-3">{mailLeft.items.map(mailRow)}</ul>}
          {(mailLeft.more > 0 || mailLeft.truncated) && (
            <a href={mailLeft.inbox_url} target="_blank" rel="noopener noreferrer" className={`${LINK} mt-3 inline-block`}>
              {mailLeft.truncated ? 'and more - open Gmail' : `and ${mailLeft.more} more - open Gmail`}
            </a>
          )}
        </>
      ) : null);
  const texts = m && body(left(m.texts, 'text', (t) => t.conversation_id),
    (items) => <ul aria-label="Texts waiting" className="space-y-3">{items.map(textRow)}</ul>,
    m.texts.status === 'ok' ? moreLink(m.texts.more, '/crm/conversations') : null);
  const missed = m && body(left(m.calls, 'call', (c) => c.conversation_id),
    (items) => <ul aria-label="Missed calls list" className="space-y-3">{items.map(callRow)}</ul>,
    m.calls.status === 'ok' ? moreLink(m.calls.more, '/crm/conversations') : null);
  const todos = m && body(m.todos,
    (items) => <ul aria-label="Tasks due" className="space-y-3">{items.map((t) => <TodoRow key={`${reads}:${t.ghl_task_id}`} todo={t} />)}</ul>,
    moreLink(m.todosMore, '/todos'));
  // ⭐ His word 2026-10-26: more than 30 days overdue goes behind ONE tap, and the button names how many (a control must
  //   name its contents - a closed list that does not say what it holds reads as the deadlines having gone).
  const deadlines = m && body(m.deadlines, (items) => {
    const { recent, older } = splitDeadlines(items, new Date());
    return (
      <>
        {recent.length > 0 && <ul aria-label="Deadlines" className="space-y-3">{recent.map((d) => <DeadlineRow key={d.deadline_id} d={d} />)}</ul>}
        {older.length > 0 && (
          <>
            <button type="button" aria-expanded={showOld} onClick={() => setShowOld((v) => !v)} className={`${LINK} mt-3 inline-block`}>
              {`Older overdue deadlines (${older.length})`}
            </button>
            {showOld && <ul aria-label="Older overdue deadlines" className="mt-3 space-y-3">{older.map((d) => <DeadlineRow key={d.deadline_id} d={d} />)}</ul>}
          </>
        )}
      </>
    );
  });

  // ── MORNING2: the chat ──
  const onDraft = useCallback((text: string, item: MorningItemRef) => {
    setRequest((r) => ({ seq: (r?.seq ?? 0) + 1, text, item }));
  }, []);
  /**
   * His iPhone findings (2026-10-28): the top bar's Assistant pressed while the page still says "Loading..." found no box
   * and did nothing - a dead button. Now the press is remembered and the box takes the focus the moment it appears.
   * (On an iPhone a focus given after the press does not raise the keyboard by itself; the box is then one tap away, in
   * view and outlined.) A read that brings no chat drops the press.
   */
  const focusAsked = useRef(false);
  const focusChat = useCallback(() => {
    const box = inputRef.current;
    if (!box) { focusAsked.current = true; return; }
    focusAsked.current = false;
    // 'nearest': the box is pinned to the bottom, so it is usually in view already and the page stays where he is.
    box.scrollIntoView?.({ block: 'nearest' });
    box.focus();
  }, []);
  // ⭐ Before the first read answers, this device's hint decides: last seen switched on, the rail stays away rather than
  //   flash on and off for him; otherwise (Robert, a new device) the rail is the rail. Then the read's answer decides.
  //   (Only `data === null` reads the hint here, so a hint re-read after a later read never moves the rail.)
  const pageHoldsChat = data === null ? chatHint && loadError === null : !!chat;
  const morningChat = useMemo<MorningChat>(() => (pageHoldsChat ? { focusChat } : {}), [pageHoldsChat, focusChat]);
  // The box is drawn whenever the chat is on - a morning with no chat yet today (no chat_id) has one too.
  const boxShown = !!(m && chat);
  useEffect(() => {
    if (!focusAsked.current) return;
    if (data === null) return; // still loading: keep the press
    focusAsked.current = false;
    if (boxShown) focusChat();
  }, [data, boxShown, focusChat]);

  return (
    <MorningChatContext.Provider value={morningChat}>
    <Layout>
      <div className="mx-auto max-w-3xl">
        {/* CHAT-DUET (his pick, 2026-10-28): the page that holds the chat reads like a chat - no title row; its Refresh is
            the round button beside the greeting. ⛔ Robert's page (no chat) keeps the title and Refresh exactly. */}
        {!pageHoldsChat && (
          <div className="mb-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">
                Morning
              </h1>
              {m && !chat && <p className="mt-1 text-sm text-gray-500">Who is waiting on you, and what you promised - most urgent first.</p>}
            </div>
            {m && (
              <div className="flex shrink-0 flex-wrap justify-end gap-2">
                <Button variant="outline" onClick={() => void load()} isLoading={loading}>Refresh</Button>
              </div>
            )}
          </div>
        )}

        {loadError && (
          <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span className="min-w-0 break-words">{loadError}</span>
            <Button size="sm" variant="outline" onClick={() => void load()} isLoading={loading}>Try again</Button>
          </div>
        )}
        {!data && !loadError && <p className="py-16 text-center text-gray-500">Loading...</p>}
        {data && !data.linked && <p className="py-10 text-sm text-gray-700">{NOT_LINKED}</p>}

        {m && chat && (
          <AssistantPanel
            mode="morning"
            lead={(
              <MorningBrief data={m} onDraft={onDraft} onChanged={retry} answering={answering} reading={loading} autoRead={autoRead} />
            )}
            request={request}
            morningChatId={chat.chat_id}
            onSendingChange={setAnswering}
            onCardSent={retry}
            inputRef={inputRef}
          />
        )}

        {m && !chat && (
          <>
            {chatWords && (
              <p role="status" className="mb-4 break-words rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                {chatWords}
              </p>
            )}
            {mail && <Section title="Waiting on you - email">{mail}</Section>}
            {texts && <Section title="Waiting on you - texts">{texts}</Section>}
            {missed && <Section title="Missed calls">{missed}</Section>}
            {todos && <Section title="Tasks">{todos}</Section>}
            {deadlines && <Section title="Deal deadlines">{deadlines}</Section>}
            {m.radar.status === 'unreadable' ? (
              <Unreadable words={m.radar.words} onRetry={retry} busy={loading} />
            ) : m.radar.line && (
              <Link to="/deal-radar" className="block break-words rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-900 hover:border-gray-400">
                {m.radar.line}
              </Link>
            )}
          </>
        )}
      </div>
    </Layout>
    </MorningChatContext.Provider>
  );
}
